import { ModelSetTransformer, ModelTransformer } from '../common/common.model';
import { MetricDataItem } from '../performance-data/metric.pd.model';

// DataConfig.Lookup_OptionalColumnType
export enum OptionalColumnType {
    CURRENT_ACTUAL = 0,
    PROJECTED_RESULT,
    PERIOD_TARGET,
    GAP,
    PROJECTED_PERCENT_TO_TARGET,
    PREVIOUS_ACTUAL,
    CURRENT_TARGET,
    GOAL,
    PERCENT_TO_TARGET,
    PEER_GROUP_ACTUAL,
    PEER_GROUP_PERCENT_TO_TARGET,
    OBJECTIVE_MET,
    WEIGHT,
    WEIGHTED_SCORE,
}

export interface ExtOptionalColumn {
    columnTypeId: number;
    name: string;
    description: string;
    label: string;
}

export interface OptionalColumn {
    id: OptionalColumnType;
    name: string;
    description: string;
    label: string;
    // TODO revise
    forced?: boolean;
    disabled?: boolean;
    hidden?: boolean;
}

class OptionalColumnModelTransformer
    implements ModelTransformer<OptionalColumn, ExtOptionalColumn>
{
    transform(model: ExtOptionalColumn): OptionalColumn {
        return {
            id: model.columnTypeId,
            name: model.name,
            description: model.description,
            label: model.label,
        };
    }
}

export class OptionalColumnModelSetTransformer extends ModelSetTransformer<
    OptionalColumn,
    ExtOptionalColumn
> {
    protected newTransformer(): ModelTransformer<
        OptionalColumn,
        ExtOptionalColumn
    > {
        return new OptionalColumnModelTransformer();
    }
}

export function validateOptionalColumns(model: OptionalColumn[]): void {
    const valid = model != null && model.length !== 0;
    if (!valid) {
        throw new Error('OptionalColumns not valid');
    }
}

export function valueForColumn(
    columnType: OptionalColumnType,
    metric: MetricDataItem,
): any {
    if (!metric) {
        return null;
    }
    switch (columnType) {
        case OptionalColumnType.CURRENT_ACTUAL:
            return metric.actual;
        case OptionalColumnType.PROJECTED_RESULT:
            return metric.projectedActual;
        case OptionalColumnType.PERIOD_TARGET:
            return metric.projectedTarget;
        case OptionalColumnType.GAP:
            return metric.gap;
        case OptionalColumnType.PROJECTED_PERCENT_TO_TARGET:
            return metric.projectedPctToTgt;
        case OptionalColumnType.PREVIOUS_ACTUAL:
            return metric.previousActual;
        case OptionalColumnType.CURRENT_TARGET:
            return metric.target;
        case OptionalColumnType.GOAL:
            return metric.goal;
        case OptionalColumnType.PERCENT_TO_TARGET:
            return metric.percentToTarget;
        case OptionalColumnType.PEER_GROUP_ACTUAL:
            return metric.actual;
        case OptionalColumnType.PEER_GROUP_PERCENT_TO_TARGET:
            return metric.percentToTarget;
        case OptionalColumnType.OBJECTIVE_MET:
            return metric.objectiveMet;
        case OptionalColumnType.WEIGHT:
            return metric.weight;
        case OptionalColumnType.WEIGHTED_SCORE:
            return metric.weightedScore;
        default:
            return null;
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function formatColumn(
    columnType: OptionalColumnType,
    value: any,
    numberTransformer: { transform: (value: any, ...args: any[]) => any },
    percentTransformer: { transform: (value: any, ...args: any[]) => any },
) {
    if (value == null) {
        return '-';
    }
    switch (columnType) {
        case OptionalColumnType.CURRENT_ACTUAL:
            return numberTransformer.transform(value, '1.2-2');
        case OptionalColumnType.PROJECTED_RESULT:
            return numberTransformer.transform(value, '1.2-2');
        case OptionalColumnType.PERIOD_TARGET:
            return numberTransformer.transform(value, '1.2-2');
        case OptionalColumnType.GAP:
            return numberTransformer.transform(value, '1.2-2');
        case OptionalColumnType.PROJECTED_PERCENT_TO_TARGET:
            return percentTransformer.transform(value, '1.2-2');
        case OptionalColumnType.PREVIOUS_ACTUAL:
            return numberTransformer.transform(value, '1.2-2');
        case OptionalColumnType.CURRENT_TARGET:
            return numberTransformer.transform(value, '1.2-2');
        case OptionalColumnType.GOAL:
            return numberTransformer.transform(value, '1.2-2');
        case OptionalColumnType.PERCENT_TO_TARGET:
            return percentTransformer.transform(value, '1.2-2');
        case OptionalColumnType.PEER_GROUP_ACTUAL:
            return numberTransformer.transform(value, '1.2-2');
        case OptionalColumnType.PEER_GROUP_PERCENT_TO_TARGET:
            return percentTransformer.transform(value, '1.2-2');
        case OptionalColumnType.OBJECTIVE_MET:
            return !value ? 'N' : 'Y';
        case OptionalColumnType.WEIGHT:
            return numberTransformer.transform(value, '1.2-2');
        case OptionalColumnType.WEIGHTED_SCORE:
            return numberTransformer.transform(value, '1.2-2');
        default:
            return null;
    }
}
